/**
 * Notification center queries. [task #5]
 *
 * Targets the §B12 backend contract (see docs/Sijilaty-Backend-Updates.md). The
 * endpoints do not exist yet (they land with task #8), so every fetcher is
 * resilient: a 404 or network error falls back to a mock set / no-op so the UI
 * renders and the bell stays interactive. Remove the MOCK_* fallbacks once
 * §B12 ships.
 *
 *   GET  <portal>/notifications?filter[read]=&page=   (paginated)
 *   GET  <portal>/notifications/unread-count          → { count }
 *   POST <portal>/notifications/{id}/read
 *   POST <portal>/notifications/read-all
 */
import { isAxiosError } from "axios";
import type { Paginated } from "../api";
import { normalizePaginated, unwrapItem } from "../apiNormalize";
import type { Portal } from "../portal";
import type { AppNotification, NotificationTone } from "@/types/notifications";
import { buildFilterQuery, get, post } from "./helpers";

/** Raw row shape as returned by the §B12 endpoint, before normalization. */
interface NotificationApiRow {
  id: number;
  type?: string | null;
  title?: string | null;
  body?: string | null;
  content?: string | null;
  tone?: string | null;
  icon?: string | null;
  target_url?: string | null;
  read_at?: string | null;
  created_at?: string | null;
}

export interface FetchNotificationsParams {
  /** Filter by read state; omit for all. */
  read?: boolean;
  page?: number;
  per_page?: number;
}

const VALID_TONES: ReadonlySet<NotificationTone> = new Set<NotificationTone>([
  "info",
  "success",
  "warning",
  "danger",
  "neutral",
]);

function toTone(value: string | null | undefined): NotificationTone {
  return value && VALID_TONES.has(value as NotificationTone)
    ? (value as NotificationTone)
    : "info";
}

/** Map a raw API row to the typed domain shape. */
function normalizeRow(row: NotificationApiRow): AppNotification {
  return {
    id: row.id,
    type: row.type ?? "generic",
    title: row.title ?? "",
    body: row.body ?? row.content ?? "",
    tone: toTone(row.tone ?? row.icon),
    targetUrl: row.target_url ?? null,
    readAt: row.read_at ?? null,
    createdAt: row.created_at ?? new Date().toISOString(),
  };
}

/** Resolve the portal-aware base path, consistent with the rest of the queries layer. */
function basePath(portal: Portal): string {
  return portal === "admin" ? "/admin/notifications" : "/central/notifications";
}

/**
 * Path for the notification-center LIST. On admin, `GET /admin/notifications` is
 * already the legacy broadcast endpoint, so the per-recipient center list is
 * served at `/admin/notifications/center` (backend §B12). unread-count / read /
 * read-all use the bare basePath on both portals.
 */
function listPath(portal: Portal): string {
  return portal === "admin" ? "/admin/notifications/center" : "/central/notifications";
}

/** True for a 404 or transport-level error — i.e. the endpoint isn't live yet. */
function isMissingEndpoint(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  // No response → network error / endpoint unreachable.
  return !err.response || err.response.status === 404;
}

// ——— Fallback mock set (REMOVE once §B12 lands) ———
// Lets the panel render with realistic content in the demo before the backend exists.
const now = Date.now();
const iso = (minutesAgo: number) => new Date(now - minutesAgo * 60_000).toISOString();

const MOCK_NOTIFICATIONS: AppNotification[] = [
  {
    id: 1,
    type: "session.created",
    title: "جلسة جديدة بانتظار المراجعة",
    body: "تم إنشاء جلسة موافقة جديدة وتحتاج إلى مراجعتك.",
    tone: "info",
    targetUrl: "/sessions",
    readAt: null,
    createdAt: iso(8),
  },
  {
    id: 2,
    type: "doctor.verified",
    title: "تم توثيق طبيب",
    body: "اكتمل توثيق ملف الطبيب بنجاح.",
    tone: "success",
    targetUrl: "/doctors",
    readAt: null,
    createdAt: iso(95),
  },
  {
    id: 3,
    type: "subscription.expiring",
    title: "اشتراكك على وشك الانتهاء",
    body: "تبقّى ٣ أيام على انتهاء اشتراكك الحالي.",
    tone: "warning",
    targetUrl: "/subscription",
    readAt: iso(60 * 26),
    createdAt: iso(60 * 26),
  },
];

function mockPaginated(params?: FetchNotificationsParams): Paginated<AppNotification> {
  let data = MOCK_NOTIFICATIONS;
  if (params?.read === true) data = data.filter((n) => n.readAt);
  if (params?.read === false) data = data.filter((n) => !n.readAt);
  return {
    data,
    meta: { total: data.length, per_page: data.length || 1, current_page: 1, last_page: 1 },
  };
}

// ——— Reads ———

export async function fetchNotifications(
  portal: Portal,
  params?: FetchNotificationsParams,
): Promise<Paginated<AppNotification>> {
  try {
    const query = buildFilterQuery({
      ...(params?.read !== undefined ? { read: params.read ? 1 : 0 } : {}),
      page: params?.page,
      per_page: params?.per_page,
    });
    const body = await get<unknown>(`${listPath(portal)}${query}`);
    const page = normalizePaginated<NotificationApiRow>(body);
    
    return { data: page.data.map(normalizeRow), meta: page.meta };
  } catch (err) {
    console.log(err);
    if (isMissingEndpoint(err)) return mockPaginated(params); // FALLBACK — remove with §B12
    throw err;
  }
}

export async function fetchUnreadCount(portal: Portal): Promise<number> {
  try {
    const body = await get<unknown>(`${basePath(portal)}/unread-count`);
    const item = unwrapItem<{ count: number }>(body);
    return Number(item.count ?? 0);
  } catch (err) {
    if (isMissingEndpoint(err)) {
      return MOCK_NOTIFICATIONS.filter((n) => !n.readAt).length; // FALLBACK — remove with §B12
    }
    throw err;
  }
}

// ——— Mutations (guarded: silently no-op when the endpoint is missing) ———

export async function markRead(portal: Portal, id: number): Promise<void> {
  try {
    await post(`${basePath(portal)}/${id}/read`, {});
  } catch (err) {
    if (isMissingEndpoint(err)) return; // FALLBACK — optimistic local update covers the UI
    throw err;
  }
}

export async function markAllRead(portal: Portal): Promise<void> {
  try {
    await post(`${basePath(portal)}/read-all`, {});
  } catch (err) {
    if (isMissingEndpoint(err)) return; // FALLBACK — optimistic local update covers the UI
    throw err;
  }
}
